import React, { useEffect, useState } from "react";
import MirrorFilter from "./components/MirrorFilter";
import { MirrorHeader } from "./components/MirrorHeader";
import { useAppDispatch, useAppSelector } from "store";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { mirrorLimits } from "configs/limits";
import {
  MirrorItem,
  handleClearDraftItem,
  type OrderFields,
  disableMirrorItem,
  enableMirrorItem,
  getMirrorListPayload,
  handlePaginate,
  handleSelectAll as handleSelectAllItems,
  handleSelect as handleSelectItem,
  setMirrorAsDefault,
} from "store/mirror/slice";
import { Table } from "components/table/Table";
import Pagination from "components/Pagination";
import { openModal } from "store/modals/slice";
import { ModalTypes } from "configs/types";
import { ListOptions } from "./components/Options/ListOptions";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import SwitchBtn from "components/buttons/SwitchBtn";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { useRefreshList } from "helpers/useRefreshList";

type Props = {};

const MirrorsList: React.FC<Props> = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [limit, setLimit] = useState<number>(mirrorLimits[1]);
  const [filterByTitle, setFilterByTitle] = useState<string>("");
  const [filterByDomain, setFilterByDomain] = useState<string>("");
  const [filterByPriority, setFilterByPriority] = useState<
    { value: string; label: string }[]
  >([]);
  const [order, setOrder] = useState<{
    [key in OrderFields]: "asc" | "desc" | "none";
  }>({
    domain: "none",
    title: "none",
    priority: "none",
    status: "none",
  });

  const mirrorList = useAppSelector((state) => state.mirror.list);
  const isMirrorListFetched = useAppSelector(
    (state) => state.mirror.isListFetched,
  );
  const totalItems = useAppSelector((state) => state.mirror.totalItems);
  const totalPages = useAppSelector((state) => state.mirror.totalPages);
  const activePage = useAppSelector((state) => state.mirror.activePage);
  const selectedItems = useAppSelector((state) => state.mirror.selectedItems);
  const processingItemId = useAppSelector(
    (state) => state.mirror.processingItemId,
  );
  const permissions = usePermissionHelper();

  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'mirror',
    isListFetched: isMirrorListFetched
  });

  useEffect(() => {
    dispatch(
      getMirrorListPayload({
        limit,
        offset: activePage - 1,
        order,
        title: filterByTitle,
        domain: filterByDomain,
        priorities: filterByPriority.map(({ value }) => value),
      }),
    );
  }, [activePage, limit, isMirrorListFetched]);

  const handlePreviousClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };

  const handleNextClick = () => {
    if (activePage < totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };

  const handleOrderClick = (key: keyof typeof order) => {
    function updateSort<T>(sort: T, key: keyof T) {
      const currentValueByKey = sort[key] as string;

      switch (currentValueByKey) {
        case "none": {
          return {
            ...sort,
            [key]: "asc",
          };
        }
        case "asc": {
          return {
            ...sort,
            [key]: "desc",
          };
        }

        case "desc": {
          return {
            ...sort,
            [key]: "none",
          };
        }

        default: {
          return {
            ...sort,
          };
        }
      }
    }

    setOrder((currentOrder) => updateSort(currentOrder, key));
    dispatch(handlePaginate(1));
  };

  const handleSelectAll = () => {
    dispatch(handleSelectAllItems());
  };

  const handleSelect = (domain: string) => {
    dispatch(handleSelectItem(domain));
  };

  const handleOpenCreateModal = () => {
    dispatch(handleClearDraftItem());
    navigate(`${location.pathname}?activeModal=${ModalTypes.mirrorCreate}`);
    dispatch(openModal({ activeModal: ModalTypes.mirrorCreate }));
  };
  const handleOpenEditModal = (domain: string) => {
    dispatch(handleClearDraftItem());
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.mirrorEdit}&entityId=${encodeURIComponent(domain)}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.mirrorEdit }));
  };

  const handleToogleItem = async (item: MirrorItem, isActive: boolean) => {
    if (isActive) {
      const result = await dispatch(disableMirrorItem(item.id));

      if (result?.payload === true) {
        dispatch(
          showNotification({
            title: t("success"),
            message: t("mirrorDisableSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } else {
        dispatch(
          showNotification({
            title: t("error"),
            message: t("mirrorDisableError"),
            type: NotificationTypes.error,
          }),
        );
      }
    } else {
      const result = await dispatch(enableMirrorItem(item.id));

      if (result?.payload === true) {
        dispatch(
          showNotification({
            title: t("success"),
            message: t("mirrorEnableSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } else {
        dispatch(
          showNotification({
            title: t("error"),
            message: t("mirrorEnableError"),
            type: NotificationTypes.error,
          }),
        );
      }
    }
  };

  const handleApplyFilter = () => {
    dispatch(handlePaginate(1));
  };

  const handleToggleIsDefault = async (id: string) => {
    dispatch(setMirrorAsDefault(id))
      .unwrap()
      .then(() => {
        dispatch(
          showNotification({
            title: t("success"),
            message: t("mirrorSetDefaultSuccess"),
            type: NotificationTypes.success,
          }),
        );
      })
      .catch(() => {
        dispatch(
          showNotification({
            title: t("error"),
            message: t("mirrorSetDefaultError"),
            type: NotificationTypes.error,
          }),
        );
      });
  };


  return (
    <div className="page-list-wrapper page-list--with-filter">
      <MirrorFilter
        filterByTitle={filterByTitle}
        filterByDomain={filterByDomain}
        filterByPriority={filterByPriority}
        onFilterByDomain={setFilterByDomain}
        onFilterSearchByTitle={setFilterByTitle}
        onFilterByPriority={setFilterByPriority}
        handleApply={handleApplyFilter}
      />
      <div className="page-list--with-filter-content">
        <MirrorHeader
          options={[]}
          createModal={handleOpenCreateModal}
          isDisabled
          isCreateAllowed={permissions?.create}
          refresh={refresh}
          isRefreshRequested={isRefreshRequested}
        />

        {!isMirrorListFetched && (
          <>
            <DataTableSkeleton columnsCount={9} rowsCount={3} />
            <PaginationSkeleton />
          </>
        )}

        {isMirrorListFetched && mirrorList.length > 0 && (
          <div className="game-table-wrap">
            <Table
              data={mirrorList}
              idField="id"
              onSort={(v) => {
                const k = v as unknown;
                handleOrderClick(k as keyof typeof order);
              }}
              order={order}
              onAllSelect={handleSelectAll}
              onSelect={handleSelect}
              selectedItems={selectedItems}
              customOptions={(itm) => (
                <ListOptions
                  id={itm.id}
                  onEdit={() => handleOpenEditModal(itm.domain)}
                  onToggle={() =>
                    handleToogleItem(itm, itm.status !== "removed")
                  }
                  isDisable={itm.status === "removed"}
                  readOnly={!permissions?.update}
                />
              )}
              customColumns={(itm) => ({
                countries: <span>{itm.countries?.join(", ") || "-"}</span>,
                isDefault:
                  itm.status === "removed" ? (
                    <div style={{ textAlign: "center" }}>-</div>
                  ) : (
                    <SwitchBtn
                      handleClick={() => handleToggleIsDefault(itm.id)}
                        isActive={itm.isDefault === 1}
                        disabled={!permissions?.update}
                    />
                  ),
              })}
              columns={{
                sortingColumns: {
                  id: {
                    value: t("id"),
                    hasSorting: false,
                  },
                },
              }}
              processingId={processingItemId}
            />
          </div>
        )}

        {isMirrorListFetched && mirrorList.length > 0 && (
          <Pagination
            handleLimitClick={(e) => {
              setLimit(e);
              dispatch(handlePaginate(1));
            }}
            limitsArray={mirrorLimits}
            activePage={activePage}
            limit={limit}
            totalItems={totalItems}
            totalPages={totalPages}
            handleNextClick={handleNextClick}
            handlePrevClick={handlePreviousClick}
          />
        )}
      </div>
    </div>
  );
};

export default MirrorsList;
